import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Injector, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PromptTemplate, TableEntryAdmin, TeilaufgabeAdmin } from '@reflact/ipkv-types';
import { MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBar } from 'primeng/progressbar';
import { TabsModule } from 'primeng/tabs';
import { ReducedEditor } from '../../../components/layout/reduced-editor/reduced-editor';
import { Shorty } from '../../../pipes/shorty';
import { FallaufgabenService } from '../../../services/fallaufgaben-service';
import { AufgabenService } from '../../../services/aufgaben-service';
import { PromptTemplateService } from '../../../services/prompt-template-service';
import { TeilaufgabenService } from '../../../services/teilaufgaben-service';
import { KiResultTable } from './ki-result-table/ki-result-table';
import { Table } from './table/table';

type RelatedTestInput = {
  type: 'standard';
  answers: string[];
} | {
  type: 'table';
  answers: string[][];
};

@Component({
  selector: 'app-teilaufgabe-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    InputNumber,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    TooltipModule,
    ContextMenuModule,
    ToggleButtonModule,
    SelectButtonModule,
    TabsModule,
    ReducedEditor,
    Shorty,
    Dialog,
    ProgressBar,
    Table,
    KiResultTable,
  ],
  templateUrl: './teilaufgabe-dialog.html',
  styleUrl: './teilaufgabe-dialog.scss',
})
export class TeilaufgabeDialog implements OnChanges {
  @ViewChild('cmTable') cmTable!: ContextMenu;
  @ViewChild('testTable') testTableComponent!: Table;

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<string>();

  protected fallaufgabenService = inject(FallaufgabenService);
  protected teilaufgabenService = inject(TeilaufgabenService);
  private injector = inject(Injector);
  private promptTemplateService = inject(PromptTemplateService);
  private messageService = inject(MessageService);

  protected promptTemplatesMap = new Map<string, PromptTemplate>();
  protected availablePromptTemplates: PromptTemplate[] = [];
  protected selectedPromptId: string | null = null;
  protected editingPromptId: string | null = null;
  protected selectedEntry: TableEntryAdmin | undefined;
  protected selectedRow: number = -1;
  protected selectedCol: number = -1;
  protected testUserAnswers: string[] = [''];
  protected testResult: { kiComment: string; kiScore: string; kiReasoning: string } | null = null;
  protected testTableResult: { kiTable: { comment: string; score: number }[]; kiReasoning: string } | null = null;
  protected testPromptMessages: { role: string; content: string }[] | null = null;
  protected lastTestAnswers: string[][] = [];
  protected testLoading = false;
  protected selectedAntwortSetIndex: number | null = null;
  protected newAntwortSetName: string = '';
  protected relatedTestInputs: Record<string, RelatedTestInput> = {};

  private get selectedTeilaufgabeWithRelated(): (TeilaufgabeAdmin & { relatedTeilaufgaben?: string[]; }) | undefined {
    return this.teilaufgabenService.selectedTeilaufgabe as (TeilaufgabeAdmin & { relatedTeilaufgaben?: string[]; }) | undefined;
  }

  protected get relatedTeilaufgabenOptions(): { label: string; value: string }[] {
    const selectedTeilaufgabe = this.selectedTeilaufgabeWithRelated;
    const aufgabenService = this.getAufgabenServiceSafe();
    if (!selectedTeilaufgabe) {
      return [];
    }

    return this.teilaufgabenService.teilaufgaben
      .filter((ta) => ta._id !== selectedTeilaufgabe._id)
      .map((ta) => {
        const aufgabe = aufgabenService?.getAufgabeById(ta.aufgabenId);
        const aufgabenNummer = aufgabe?.aufgabenIndex ?? "?";
        const label = `${aufgabenNummer}.${ta.teilaufgabenIndex} | ${this.getPlainText(ta.aufgabenstellung)}`;
        return { label, value: ta._id };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }

  protected get selectedRelatedTeilaufgabenForTest(): (TeilaufgabeAdmin & { relatedTeilaufgaben?: string[]; })[] {
    const selected = this.selectedRelatedTeilaufgaben;
    if (selected.length === 0) {
      return [];
    }

    return selected
      .map((id) => this.teilaufgabenService.getTeilAufgabeById(id) as (TeilaufgabeAdmin & { relatedTeilaufgaben?: string[]; }) | undefined)
      .filter((ta): ta is (TeilaufgabeAdmin & { relatedTeilaufgaben?: string[]; }) => !!ta);
  }

  protected getRelatedTitle(teilaufgabe: TeilaufgabeAdmin): string {
    const aufgabenService = this.getAufgabenServiceSafe();
    const aufgabe = aufgabenService?.getAufgabeById(teilaufgabe.aufgabenId);
    const aufgabenNummer = aufgabe?.aufgabenIndex ?? '?';
    return `${aufgabenNummer}.${teilaufgabe.teilaufgabenIndex} | ${this.getPlainText(teilaufgabe.aufgabenstellung)}`;
  }

  protected getRelatedStandardAnswers(teilaufgabeId: string): string[] {
    const current = this.relatedTestInputs[teilaufgabeId];
    if (!current || current.type !== 'standard') {
      return [];
    }
    return current.answers;
  }

  protected getRelatedTableAnswers(teilaufgabeId: string): string[][] {
    const current = this.relatedTestInputs[teilaufgabeId];
    if (!current || current.type !== 'table') {
      return [];
    }
    return current.answers;
  }

  private getAufgabenServiceSafe(): AufgabenService | null {
    try {
      return this.injector.get(AufgabenService);
    } catch (_error) {
      return null;
    }
  }

  protected get selectedRelatedTeilaufgaben(): string[] {
    return this.selectedTeilaufgabeWithRelated?.relatedTeilaufgaben ?? [];
  }

  protected set selectedRelatedTeilaufgaben(value: string[]) {
    if (!this.selectedTeilaufgabeWithRelated) {
      return;
    }
    this.selectedTeilaufgabeWithRelated.relatedTeilaufgaben = value ?? [];
    this.syncRelatedTestInputs();
  }

  protected get antwortSetOptions(): { label: string; value: number }[] {
    const sets = this.teilaufgabenService.selectedTeilaufgabe?.testAnswerSets ?? [];
    return sets.map((s, i) => ({ label: s.name, value: i }));
  }

  protected context: MenuItem[] = [
    { label: 'Editierbar', icon: 'pi pi-pencil', command: () => this.changeEntryEditSetting() },
    { label: 'Überschrift', icon: 'ri-news-line ri-lg', command: () => this.changeEntryHeaderSetting() },
    { label: 'Spalte entfernen', icon: 'ri-delete-column ri-lg', command: () => this.deleteColumnFromTable() },
    { label: 'Zeile entfernen', icon: 'ri-delete-row ri-lg', command: () => this.deleteRowFromTable() }
  ];

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']?.currentValue === true && this.selectedTeilaufgabeWithRelated) {
      this.selectedTeilaufgabeWithRelated.relatedTeilaufgaben = this.selectedTeilaufgabeWithRelated.relatedTeilaufgaben ?? [];
      this.syncRelatedTestInputs();
      await this.loadPromptTemplates();
      await this.loadAvailablePromptTemplates();
    }
  }

  private syncRelatedTestInputs() {
    const selectedIds = new Set(this.selectedRelatedTeilaufgaben);
    const nextInputs: Record<string, RelatedTestInput> = {};

    for (const teilaufgabeId of selectedIds) {
      const teilaufgabe = this.teilaufgabenService.getTeilAufgabeById(teilaufgabeId);
      if (!teilaufgabe) {
        continue;
      }

      const existing = this.relatedTestInputs[teilaufgabeId];
      if (teilaufgabe.type === 'standard') {
        const answers = existing?.type === 'standard'
          ? [...existing.answers]
          : [];
        while (answers.length < teilaufgabe.answersCount) {
          answers.push('');
        }
        nextInputs[teilaufgabeId] = {
          type: 'standard',
          answers: answers.slice(0, teilaufgabe.answersCount)
        };
      } else {
        const rows = teilaufgabe.table.length;
        const cols = teilaufgabe.table[0]?.length ?? 0;
        const oldTable = existing?.type === 'table' ? existing.answers : [];
        const answers: string[][] = Array.from({ length: rows }, (_, rowIndex) =>
          Array.from({ length: cols }, (_, colIndex) => oldTable[rowIndex]?.[colIndex] ?? '')
        );
        nextInputs[teilaufgabeId] = {
          type: 'table',
          answers
        };
      }
    }

    this.relatedTestInputs = nextInputs;
  }

  private buildRelatedAnswersPayload(): Record<string, RelatedTestInput> | undefined {
    const payload: Record<string, RelatedTestInput> = {};
    for (const teilaufgabe of this.selectedRelatedTeilaufgabenForTest) {
      const entry = this.relatedTestInputs[teilaufgabe._id];
      if (!entry) {
        continue;
      }
      payload[teilaufgabe._id] = entry;
    }

    return Object.keys(payload).length > 0 ? payload : undefined;
  }

  private getPlainText(value: string): string {
    return (value || "")
      .replaceAll(/<[^>]+>/g, " ")
      .replaceAll(/\s+/g, " ")
      .trim();
  }

  private async loadPromptTemplates() {
    if (!this.teilaufgabenService.selectedTeilaufgabe) return;
    const templates = await this.promptTemplateService.listForTeilaufgabe(this.teilaufgabenService.selectedTeilaufgabe._id);
    this.promptTemplatesMap.clear();
    for (const t of templates) {
      console.log("Templates", t);
      this.promptTemplatesMap.set(t._id, t);
    }
  }

  private async loadAvailablePromptTemplates() {
    if (!this.fallaufgabenService.selectedFallaufgabe.category) return;
    try {
      this.availablePromptTemplates = await this.promptTemplateService.list(this.fallaufgabenService.selectedFallaufgabe.category);
    } catch (error) {
      console.error('Error loading available prompt templates:', error);
      this.availablePromptTemplates = [];
    }
  }

  protected get filteredPromptTemplates(): PromptTemplate[] {
    const usedIds = this.teilaufgabenService.selectedTeilaufgabe?.aiSystemPromts ?? [];
    return this.availablePromptTemplates.filter(t => !usedIds.includes(t._id));
  }

  protected getPromptText(id: string): string {
    return this.promptTemplatesMap.get(id)?.prompt ?? id;
  }

  protected isPromptEditable(promptId: string): boolean {
    const template = this.promptTemplatesMap.get(promptId);
    return template ? !template.category : false;
  }

  protected toggleEditMode(promptId: string) {
    this.editingPromptId = this.editingPromptId === promptId ? null : promptId;
  }

  protected isEditingPrompt(promptId: string): boolean {
    return this.editingPromptId === promptId;
  }

  protected async updatePromptTemplate(promptId: string) {
    const template = this.promptTemplatesMap.get(promptId);
    if (!template) return;

    try {
      await this.promptTemplateService.update({
        _id: template._id,
        name: template.name,
        prompt: template.prompt
      });
      this.editingPromptId = null;
      this.messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Prompt gespeichert' });
    } catch (error) {
      console.error('Error updating prompt template:', error);
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Prompt konnte nicht aktualisiert werden' });
    }
  }

  protected addTestAnswer() {
    this.testUserAnswers.push('');
  }

  protected removeTestAnswer(index: number) {
    this.testUserAnswers.splice(index, 1);
  }

  protected async testKiResult() {
    if (!this.teilaufgabenService.selectedTeilaufgabe) return;
    this.testLoading = true;
    this.testResult = null;
    this.testTableResult = null;
    this.testPromptMessages = null;
    try {
      const isTable = this.teilaufgabenService.selectedTeilaufgabe.type === 'table';
      const userAnswers = isTable
        ? this.testTableComponent.getAnswers()
        : this.testUserAnswers;
      const relatedAnswers = this.buildRelatedAnswersPayload();

      if (isTable) {
        this.lastTestAnswers = userAnswers as string[][];
        const result = relatedAnswers
          ? await this.teilaufgabenService.getKiResultTable(
            this.teilaufgabenService.selectedTeilaufgabe._id,
            userAnswers,
            relatedAnswers
          )
          : await this.teilaufgabenService.getKiResultTable(
            this.teilaufgabenService.selectedTeilaufgabe._id,
            userAnswers
          );
        this.testTableResult = {
          kiTable: result.kiTable ?? [],
          kiReasoning: result.kiReasoning
        };
        this.testPromptMessages = result.promptMessages ?? null;
      } else {
        const result = relatedAnswers
          ? await this.teilaufgabenService.getKiResult(
            this.teilaufgabenService.selectedTeilaufgabe._id,
            userAnswers,
            relatedAnswers
          )
          : await this.teilaufgabenService.getKiResult(
            this.teilaufgabenService.selectedTeilaufgabe._id,
            userAnswers
          );
        this.testResult = result;
        this.testPromptMessages = result?.promptMessages ?? null;
      }
    } catch (error) {
      console.error('Error testing KI result:', error);
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'KI-Test fehlgeschlagen' });
    } finally {
      this.testLoading = false;
    }
  }

  protected async saveAntwortSet() {
    const ta = this.teilaufgabenService.selectedTeilaufgabe;
    if (!ta) return;
    const name = this.newAntwortSetName.trim() || `Antwortset ${(ta.testAnswerSets?.length ?? 0) + 1}`;
    const relatedTestInputs = this.relatedTestInputs;
    if (ta.type === 'standard') {
      ta.testAnswerSets = [...(ta.testAnswerSets ?? []), { name, answers: [...this.testUserAnswers], relatedTestInputs }];
    } else {
      const answers = this.testTableComponent.getAnswers();
      ta.testAnswerSets = [...(ta.testAnswerSets ?? []), { name, answers, relatedTestInputs }];
    }
    this.newAntwortSetName = '';
    await this.teilaufgabenService.updateTestAnswerSets();
    this.messageService.add({ severity: 'success', summary: 'Erfolg', detail: `Antwortset "${name}" gespeichert` });
  }

  protected loadAntwortSet() {
    const ta = this.teilaufgabenService.selectedTeilaufgabe;
    if (!ta || this.selectedAntwortSetIndex == null) return;
    const set = ta.testAnswerSets?.[this.selectedAntwortSetIndex];
    if (!set) return;
    if (ta.type === 'standard') {
      this.testUserAnswers = [...(set as { name: string; answers: string[] }).answers];
    } else {
      const tableAnswers = (set as { name: string; answers: string[][] }).answers;
      this.testTableComponent.setAnswers(tableAnswers);
    }
    // Load related test inputs if they exist
    const relatedTestInputs = (set as { relatedTestInputs?: Record<string, RelatedTestInput> }).relatedTestInputs;
    if (relatedTestInputs) {
      this.relatedTestInputs = { ...relatedTestInputs };
    } else {
      this.relatedTestInputs = {};
    }
    this.messageService.add({ severity: 'info', summary: 'Geladen', detail: `Antwortset "${set.name}" geladen` });
  }

  protected async deleteAntwortSet() {
    const ta = this.teilaufgabenService.selectedTeilaufgabe;
    if (!ta || this.selectedAntwortSetIndex == null) return;
    const name = ta.testAnswerSets?.[this.selectedAntwortSetIndex]?.name;
    ta.testAnswerSets?.splice(this.selectedAntwortSetIndex, 1);
    ta.testAnswerSets = [...(ta.testAnswerSets ?? [])];
    this.selectedAntwortSetIndex = null;
    await this.teilaufgabenService.updateTestAnswerSets();
    this.messageService.add({ severity: 'warn', summary: 'Gelöscht', detail: `Antwortset "${name}" gelöscht` });
  }

  protected async overwriteAntwortSet() {
    const ta = this.teilaufgabenService.selectedTeilaufgabe;
    if (!ta || this.selectedAntwortSetIndex == null) return;
    const set = ta.testAnswerSets?.[this.selectedAntwortSetIndex];
    if (!set) return;
    if (ta.type === 'standard') {
      (set as { name: string; answers: string[] }).answers = [...this.testUserAnswers];
    } else {
      (set as { name: string; answers: string[][] }).answers = this.testTableComponent.getAnswers();
    }
    // Also save related test inputs
    (set as { relatedTestInputs?: Record<string, RelatedTestInput> }).relatedTestInputs = this.relatedTestInputs;
    ta.testAnswerSets = [...(ta.testAnswerSets ?? [])];
    await this.teilaufgabenService.updateTestAnswerSets();
    this.messageService.add({ severity: 'success', summary: 'Überschrieben', detail: `Antwortset "${set.name}" überschrieben` });
  }

  protected close() {
    console.log("CLOSE")

    this.visibleChange.emit(false);
    this.teilaufgabenService.reset();
  }

  protected async save() {
    console.log("SAVEEEEEE")
    this.visibleChange.emit(false);
    await this.updateTeilaufgabe();
  }

  protected async updateTeilaufgabe() {
    console.log("template is ", this.teilaufgabenService.selectedTeilaufgabe)
    if (!this.teilaufgabenService.selectedTeilaufgabe) return;

    for (const promptId of this.teilaufgabenService.selectedTeilaufgabe.aiSystemPromts) {
      const template = this.promptTemplatesMap.get(promptId)
      console.log("template is ", template)
    }

    const aufgabenId = this.teilaufgabenService.selectedTeilaufgabe.aufgabenId;
    // HTML-Cleaning: Tabellen-HTML bewahren, aber <p>-Tags außerhalb der Tabelle entfernen
    const cleanHtml = (html: string): string => {
      if (!html) return html;
      let cleaned = html.replaceAll("&nbsp;", ' ');

      // Wenn Tabellen vorhanden sind, nur außerhalb der Tabelle <p>-Tags entfernen
      if (cleaned.includes('quill-better-table-wrapper')) {
        const tableRegex = /<div class="quill-better-table-wrapper">[\s\S]*?<\/div>/;
        const match = cleaned.match(tableRegex);

        if (match) {
          const table = match[0];
          const beforeTable = cleaned.substring(0, match.index);
          const afterTable = cleaned.substring((match.index || 0) + table.length);

          // Hilfsfunktion für <p>-Tag-Bereinigung
          const cleanPTags = (text: string) => text.replaceAll(/<p[^>]*>/g, '').replaceAll('</p>', '<br>');

          return cleanPTags(beforeTable) + table + cleanPTags(afterTable);
        }
      }

      // Wenn keine Tabellen vorhanden: Standard-Bereinigung
      return cleaned.replaceAll(/<p[^>]*>/g, '').replaceAll('</p>', '<br>');
    };
    this.teilaufgabenService.selectedTeilaufgabe.aufgabenstellung = cleanHtml(this.teilaufgabenService.selectedTeilaufgabe.aufgabenstellung);
    this.teilaufgabenService.selectedTeilaufgabe.korrekturhinweise = cleanHtml(this.teilaufgabenService.selectedTeilaufgabe.korrekturhinweise);

    if (this.teilaufgabenService.selectedTeilaufgabe.type === 'standard') {
      for (const solution of this.teilaufgabenService.selectedTeilaufgabe.solutions) {
        solution.text = cleanHtml(solution.text);
      }
    }
    await this.teilaufgabenService.update();
    this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Teilaufgabe wurde aktualisiert.`, life: 3000 });
    this.teilaufgabenService.reset();
    this.saved.emit(aufgabenId);
  }

  protected async addPromptFromSelection() {
    if (!this.teilaufgabenService.selectedTeilaufgabe || !this.selectedPromptId) return;
    if (this.fallaufgabenService.selectedFallaufgabe.state === 'draft') {
      // Check if prompt is already added
      if (!this.teilaufgabenService.selectedTeilaufgabe.aiSystemPromts.includes(this.selectedPromptId)) {
        this.teilaufgabenService.selectedTeilaufgabe.aiSystemPromts.push(this.selectedPromptId);
        // Add to map for immediate display
        const template = this.availablePromptTemplates.find(t => t._id === this.selectedPromptId);
        if (template) {
          this.promptTemplatesMap.set(template._id, template);
        }
      }
      this.selectedPromptId = null; // Reset selection
    }
  }

  protected async addNewAiSystemPrompt() {
    if (!this.teilaufgabenService.selectedTeilaufgabe) return;
    if (this.fallaufgabenService.selectedFallaufgabe.state === 'draft') {
      const prompt = await this.promptTemplateService.create("Neuer KI Korrektur Prompt", "Neuer KI Korrektur Prompt", [], null);
      this.promptTemplatesMap.set(prompt._id, prompt);
      this.teilaufgabenService.selectedTeilaufgabe.aiSystemPromts.push(prompt._id);
    }
  }

  protected async addEmptySolution() {
    if (!this.teilaufgabenService.selectedTeilaufgabe) return;
    if (this.teilaufgabenService.selectedTeilaufgabe.type === 'table') return;
    if (this.fallaufgabenService.selectedFallaufgabe.state === 'draft') {
      this.teilaufgabenService.selectedTeilaufgabe.solutions.push({ text: 'Neue Lösung', points: 0 });
    }
  }

  protected async deletePrompt(index: number) {
    if (!this.teilaufgabenService.selectedTeilaufgabe) return;
    if (this.fallaufgabenService.selectedFallaufgabe.state === 'draft') {
      this.teilaufgabenService.selectedTeilaufgabe.aiSystemPromts.splice(index, 1);
    }
  }

  protected async deleteSolution(index: number) {
    if (!this.teilaufgabenService.selectedTeilaufgabe) return;
    if (this.teilaufgabenService.selectedTeilaufgabe.type === 'table') return;
    if (this.fallaufgabenService.selectedFallaufgabe.state === 'draft') {
      this.teilaufgabenService.selectedTeilaufgabe.solutions.splice(index, 1);
    }
  }

  protected onContextMenuTable(event: any, row: number, entry: TableEntryAdmin) {
    if (this.teilaufgabenService.selectedTeilaufgabe?.type === 'table') {
      this.cmTable.target = event.currentTarget;
      this.selectedEntry = entry;
      this.selectedRow = row;
      this.selectedCol = this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow].findIndex(e => e === this.selectedEntry);
      this.cmTable.show(event);
    }
  }

  protected onContextMenuTableHide() {
    this.selectedEntry = undefined;
    this.selectedRow = -1;
    this.selectedCol = -1;
  }

  protected changeEntryHeaderSetting() {
    if (this.selectedEntry && this.teilaufgabenService.selectedTeilaufgabe?.type === 'table') {
      if (this.selectedEntry.isEditable) {
        let temp: TableEntryAdmin = { isEditable: false, isHeader: true, value: this.selectedEntry.solution };
        this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol] = temp;
      } else {
        this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol].isHeader = !this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol].isHeader;
      }
    }
  }

  protected changeEntryEditSetting() {
    if (this.selectedEntry && this.teilaufgabenService.selectedTeilaufgabe?.type === 'table') {
      if (this.selectedEntry.isHeader) {
        let temp: TableEntryAdmin = { isEditable: true, isHeader: false, solution: this.selectedEntry.value };
        this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol] = temp;
      } else {
        let content: string = '';
        //@ts-ignore
        if (this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol].value) content = this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol].value;
        this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol].isEditable = !this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol].isEditable;
        //@ts-ignore
        if (content !== '') this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow][this.selectedCol].solution = content;
      }
    }
  }

  protected deleteColumnFromTable() {
    if (this.teilaufgabenService.selectedTeilaufgabe?.type === 'table') {
      const selectedCol = this.teilaufgabenService.selectedTeilaufgabe.table[this.selectedRow].findIndex(e => e === this.selectedEntry);
      if (selectedCol !== -1) {
        for (const row of this.teilaufgabenService.selectedTeilaufgabe.table) {
          row.splice(selectedCol, 1);
        }
      }
    }
  }

  protected addColumnToTable() {
    if (this.teilaufgabenService.selectedTeilaufgabe?.type === 'table') {
      for (const row of this.teilaufgabenService.selectedTeilaufgabe.table) {
        row.push({ isEditable: false, isHeader: false, value: '' });
      }
    }
  }

  protected deleteRowFromTable() {
    if (this.teilaufgabenService.selectedTeilaufgabe?.type === 'table') {
      this.teilaufgabenService.selectedTeilaufgabe.table.splice(this.selectedRow, 1);
    }
  }

  protected addRowToTable() {
    if (this.teilaufgabenService.selectedTeilaufgabe?.type === 'table') {
      const rowLength = this.teilaufgabenService.selectedTeilaufgabe.table[0].length;
      const newRow: TableEntryAdmin[] = [];
      for (let index = 0; index < rowLength; index++) {
        newRow.push({ isEditable: false, isHeader: false, value: '' });
      }
      this.teilaufgabenService.selectedTeilaufgabe.table.push(newRow);
    }
  }
}
